import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const currentUser = (() => {
  try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); }
  catch { return {}; }
})();
const isAdmin = currentUser?.role === 'admin';

const fmt = (cents) =>
  '$' + (Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px', marginBottom: 12 };
const empty = (msg) => (
  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>{msg}</div>
);

export default function AdminReconciliation() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [processing, setProcessing]     = useState({});

  const headers = { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` };

  const fetchTransactions = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/agents/finance/transactions`, { headers });
      setTransactions(res.data || []);
    } catch (err) {
      console.error('Transactions fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTransactions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMatch = async (id, matched) => {
    setProcessing(p => ({ ...p, [`t${id}`]: true }));
    try {
      await axios.patch(`${API}/api/admin/agents/finance/transactions/${id}/match`, { matched }, { headers });
      await fetchTransactions();
    } catch (err) {
      alert(err?.response?.data?.error || err.message || 'Action failed.');
    } finally {
      setProcessing(p => ({ ...p, [`t${id}`]: false }));
    }
  };

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>Finance</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Reconciliation</h1>
        </div>

        {loading && <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>Loading…</div>}

        {!loading && (
          <>
            {isAdmin && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.post(
                        `${API}/api/admin/agents/finance/transactions/auto-match`,
                        {}, { headers }
                      );
                      alert(`Auto-match complete: ${res.data.matched} matched, ${res.data.failed} unmatched (of ${res.data.total}).`);
                      await fetchTransactions();
                    } catch (err) {
                      alert('Auto-match failed: ' + (err?.response?.data?.error || err.message));
                    }
                  }}
                  style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Run Auto-Match
                </button>
              </div>
            )}
            {transactions.length === 0 && empty('No transactions yet')}
            {transactions.map(t => {
              const out = t.direction === 'outbound';
              const busy = !!processing[`t${t.id}`];
              return (
                <div key={t.id} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{t.counterparty || '—'}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>
                      {fmtDate(t.transaction_date)} · {t.category || 'Uncategorized'}{t.description ? ` · ${t.description}` : ''}
                    </div>
                    {t.loan_borrower && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Loan: {t.loan_borrower}</div>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: out ? '#991b1b' : '#166534' }}>
                      {out ? '−' : '+'}{fmt(t.amount_cents)}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', borderRadius: 999, padding: '2px 8px', background: t.matched ? '#f0fde4' : '#f3f4f6', color: t.matched ? '#166534' : '#6b7280' }}>
                      {t.matched ? '✓ Matched' : 'Unmatched'}
                    </span>
                    {isAdmin && (
                      <button disabled={busy} onClick={() => handleMatch(t.id, !t.matched)}
                        style={{ background: '#fff', color: '#3a6ad9', border: '1px solid #3a6ad9', borderRadius: 7, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                        {t.matched ? 'Unmatch' : 'Match'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
